import { useState } from 'react'
import { PageMeta } from '../components/seo/PageMeta'
import { Button } from '../components/ui/Button'
import { SectionLabel } from '../components/ui/SectionLabel'
import { pageMeta } from '../lib/seo'

type MatrixRow = {
  label: string
  values: string[]
  /** For rows where "No" is the favorable answer (e.g. "Data used for training by default"). */
  invertHighlight?: boolean
}

type MatrixCategory = {
  name: string
  rows: MatrixRow[]
}

type PrivacyQuote = {
  label: string
  text: string
}

type ProviderColor = 'claude' | 'chatgpt' | 'gemini' | 'copilot' | 'meta' | 'grok'

type PrivacyProvider = {
  agent: string
  color: ProviderColor
  source: string
  url: string
  quotes: PrivacyQuote[]
}

const data: { agents: string[]; categories: MatrixCategory[] } = {
  agents: ['Claude (Anthropic)', 'ChatGPT (OpenAI)', 'Gemini (Google)', 'Copilot (Microsoft)', 'Meta AI', 'Grok (xAI)'],
  categories: [
    {
      name: 'Privacy & Data',
      rows: [
        {
          label: 'Data used for training by default',
          invertHighlight: true,
          values: [
            'Yes (all consumer plans)',
            'Yes (consumer plans)',
            'Yes (consumer plans)',
            'No',
            'Yes',
            'Yes (chats + public X posts)',
          ],
        },
        {
          label: 'How to opt out of training',
          values: [
            'Settings toggle (all consumer tiers)',
            'Settings toggle (Data Controls)',
            'Turn off Gemini Apps Activity',
            'N/A (not trained on chats)',
            'EU/UK objection form only',
            'Settings toggle or Private Chat mode',
          ],
        },
        {
          label: 'Free tier opt-out available',
          values: [
            'Yes (settings toggle)',
            'Yes (settings toggle)',
            'Yes (activity toggle)',
            'N/A',
            'No (US)',
            'Yes (logged in; not when logged out)',
          ],
        },
        {
          label: 'Enterprise privacy guarantee',
          values: [
            'Yes (Claude for Teams/Enterprise)',
            'Yes (ChatGPT Enterprise)',
            'Yes (Workspace Enterprise)',
            'Yes (Copilot for M365)',
            'No',
            'Yes (Enterprise ToS + ZDR option)',
          ],
        },
        {
          label: 'Data retention (default)',
          values: [
            '5 yrs (training on), 30 days (opted out)',
            'Indefinite (chat), 30 days (API)',
            'Up to 18 months',
            'Varies by plan',
            'Unclear',
            'While account active; deleted in 30 days',
          ],
        },
      ],
    },
    {
      name: 'Subscription Plans',
      rows: [
        {
          label: 'Free tier',
          values: ['Yes', 'Yes', 'Yes', 'Yes (Copilot Chat)', 'Yes', 'Yes'],
        },
        {
          label: 'Paid personal plan (monthly)',
          values: [
            'Pro $20 · Max $100–$200',
            'Go $8 · Plus $20 · Pro $100–$200',
            'AI Plus $4.99 · Pro $19.99 · Ultra $99.99–$199.99',
            'M365 Premium $19.99 (Copilot Pro retired)',
            'None',
            'SuperGrok Lite $10 · SuperGrok $30 · Heavy $300',
          ],
        },
        {
          label: 'Team/Business plan',
          values: [
            'Team $25–$125/user',
            'Business $20–$25/user',
            'Workspace (Business, custom)',
            'Copilot Business $21/user · M365 Copilot $30/user',
            'Business (Meta)',
            'Custom (via sales)',
          ],
        },
        {
          label: 'Enterprise plan',
          values: ['Yes (custom)', 'Yes (custom)', 'Yes (custom)', 'Yes ($30/user + M365 base)', 'Limited', 'Yes (custom)'],
        },
      ],
    },
    {
      name: 'Core Features',
      rows: [
        {
          label: 'Web search',
          values: ['Yes (paid)', 'Yes (free & paid)', 'Yes (free & paid)', 'Yes (free & paid)', 'Yes (free)', 'Yes (web + X search)'],
        },
        {
          label: 'Image generation',
          values: ['No', 'Yes (DALL-E, paid)', 'Yes (Imagen, paid)', 'Yes (DALL-E, paid)', 'Yes (free)', 'Yes (Grok Imagine)'],
        },
        {
          label: 'Image understanding (vision)',
          values: ['Yes', 'Yes', 'Yes', 'Yes', 'Yes', 'Yes'],
        },
        {
          label: 'File / document upload',
          values: ['Yes', 'Yes', 'Yes', 'Yes', 'Limited', 'Yes'],
        },
        {
          label: 'Code execution',
          values: ['Yes', 'Yes', 'Yes', 'Yes', 'No', 'Yes'],
        },
        {
          label: 'Voice mode',
          values: ['Yes (mobile)', 'Yes (Advanced Voice)', 'Yes', 'Yes', 'Yes', 'Yes'],
        },
        {
          label: 'Memory / personalization',
          values: ['Limited (opt-in)', 'Yes (opt-in)', 'Yes (opt-in)', 'Limited', 'Yes', 'Yes'],
        },
        {
          label: 'Plugins / integrations',
          values: ['Yes (MCP, API)', 'Yes (GPT Store)', 'Yes (Extensions)', 'Yes (M365 apps)', 'Limited', 'Yes (Connectors, X platform)'],
        },
        {
          label: 'API access',
          values: ['Yes', 'Yes', 'Yes', 'Yes (Azure)', 'Yes (limited)', 'Yes'],
        },
        {
          label: 'Agents / Agentic tasks',
          values: [
            'Yes (Claude Code, MCP)',
            'Yes (Codex, GPTs, Ultra mode)',
            'Yes (Gemini Spark, Jules, Antigravity)',
            'Yes (Copilot agents, Studio)',
            'Yes (Muse Spark thinking mode)',
            'Yes (Grok Build, multi-agent Heavy)',
          ],
        },
      ],
    },
    {
      name: 'Model & Performance',
      rows: [
        {
          label: 'Latest model',
          values: [
            'Claude Fable 5.1 / Opus 5 / Sonnet 5',
            'GPT-6 Astra / GPT-5.6 (Sol / Terra / Luna)',
            'Gemini 3.8 Flash / 3.1 Pro (3.5 Pro not released)',
            'GPT-5.X (via Azure)',
            'Muse Spark 1.3',
            'Grok 4.6',
          ],
        },
        {
          label: 'Context window (max)',
          values: [
            '1M tokens',
            '1.05M tokens',
            '1M tokens',
            'Varies (up to 400K in Codex)',
            '1M (Muse Spark 1.3); 10M (Llama 4 Scout)',
            '500K (Grok 4.6), 1M (Grok 4.3)',
          ],
        },
        {
          label: 'Multimodal (text, image, audio)',
          values: [
            'Text + Image',
            'Text + Image + Audio',
            'Text + Image + Audio + Video',
            'Text + Image + Audio',
            'Text + Image',
            'Text + Image (+ audio/video via dedicated models)',
          ],
        },
        {
          label: 'Open source option',
          values: ['No', 'No', 'No', 'No', 'Limited (Llama 4 open-weight; Muse Spark closed)', 'No (older Grok-1 weights only)'],
        },
      ],
    },
    {
      name: 'Token Economics',
      rows: [
        {
          label: 'Flagship model (API, per 1M tokens in / out)',
          values: [
            'Fable 5.1: $10 / $50',
            'GPT-6 Astra: $10 / $50 · Sol: $4 / $20 (promo thru Nov 2026)',
            '3.1 Pro: $2 / $12 (3.5 Pro not released)',
            'No per-token API (Azure OpenAI rates)',
            'Muse Spark 1.3: $1.25 / $4.25',
            'Grok 4.6: $2 / $6 (2x above 200K prompt)',
          ],
        },
        {
          label: 'Workhorse model (API, per 1M tokens in / out)',
          values: [
            'Sonnet 5: $2 / $10',
            'GPT-5.6 Terra: $2 / $12',
            '3.8 Flash: $0.75 / $3.75 (intro thru Dec 2026)',
            'Copilot Credits: $0.01/credit (Studio)',
            'Llama 4 Maverick: ~$0.15–$0.50 via hosts',
            'Grok 4.3: $1.25 / $2.50',
          ],
        },
        {
          label: 'Budget model (API, per 1M tokens in / out)',
          values: [
            'Haiku 4.5: $1 / $5',
            'GPT-5.6 Luna: $0.20 / $1.20',
            '3.1 Flash-Lite: $0.25 / $1.50',
            'N/A (seat + credit model)',
            'Llama 4 Scout: ~$0.08–$0.20 via hosts',
            'Grok 4 Fast: $0.20 / $0.50',
          ],
        },
        {
          label: 'Batch processing discount',
          values: ['50% off', '50% off (Batch/Flex)', '50% off', 'N/A', 'Varies by host', '20% off (not on Grok 4.5)'],
        },
        {
          label: 'Prompt caching discount',
          values: [
            'Up to 90% off cached input',
            'Yes (cached input discount)',
            'Up to 90% off cached input',
            'N/A',
            'Varies by host',
            '~85% off cached input',
          ],
        },
      ],
    },
  ],
}

const privacyQuotes: PrivacyProvider[] = [
  {
    agent: 'Claude (Anthropic)',
    color: 'claude',
    source: 'Anthropic Privacy Policy & Usage Policy',
    url: 'https://www.anthropic.com/legal/privacy',
    quotes: [
      {
        label: 'On training data (consumer plans)',
        text: 'We may use your Inputs and Outputs to train and improve Anthropic AI models, unless you opt out through your account settings. Even if you opt out, we will use Inputs and Outputs for model improvement when your conversations are flagged for safety review.',
      },
      {
        label: 'On data retention',
        text: 'If you allow us to use your data for model training, data retention extends to five years. If you do not choose to provide your data for model training, you\'ll continue with our existing 30-day data retention period.',
      },
      {
        label: 'On API / enterprise',
        text: 'We do not train on data submitted through the API by default. For Claude for Enterprise customers, we provide contractual commitments that your data will not be used to train our models, and we maintain strict data isolation between customers.',
      },
    ],
  },
  {
    agent: 'ChatGPT (OpenAI)',
    color: 'chatgpt',
    source: 'OpenAI Privacy Policy & FAQ',
    url: 'https://openai.com/policies/privacy-policy',
    quotes: [
      {
        label: 'On training data (free tier)',
        text: 'We use the information you provide us to improve our Services. This includes information you provide when you use our Services, such as your prompts and responses.',
      },
      {
        label: 'On opting out',
        text: "If you turn off your chat history for a conversation, we won't use those conversations to train our models... You can turn off training in Settings > Data Controls > Improve the model for everyone.",
      },
      {
        label: 'On ChatGPT Enterprise / API',
        text: 'OpenAI does not train on your business data or conversations by default for ChatGPT Enterprise and API customers. Your inputs and outputs are not used to train OpenAI models unless you explicitly opt in.',
      },
    ],
  },
  {
    agent: 'Gemini (Google)',
    color: 'gemini',
    source: 'Google Gemini Apps Privacy Notice',
    url: 'https://support.google.com/gemini/answer/13594961',
    quotes: [
      {
        label: 'On training data (free tier)',
        text: "Human reviewers may read, annotate, and process your Gemini Apps conversations... Your Gemini Apps conversations are not used to show you ads. Conversations are stored for up to 18 months by default.",
      },
      {
        label: 'On opting out',
        text: "You can turn off Gemini Apps Activity at any time. When Gemini Apps Activity is off, your conversations won't be saved to your account and won't be used to improve Google's AI models.",
      },
      {
        label: 'On Google Workspace / Enterprise',
        text: "When you use Gemini as a Workspace user, your data is not used to train Google's foundational models without your permission. Workspace customer data is subject to the Google Cloud Data Processing Amendment.",
      },
    ],
  },
  {
    agent: 'Copilot (Microsoft)',
    color: 'copilot',
    source: 'Microsoft Privacy Statement & Copilot FAQ',
    url: 'https://privacy.microsoft.com/en-us/privacystatement',
    quotes: [
      {
        label: 'On not training on chat data',
        text: 'Microsoft does not use your Copilot conversations to train foundation AI models. Your prompts and responses are not used to train the underlying large language models.',
      },
      {
        label: 'On data handling',
        text: 'Microsoft Copilot uses Azure OpenAI Service, which is built on enterprise-grade security, compliance, and privacy commitments... Your data is not available to other customers and is not used to improve base models.',
      },
      {
        label: 'On Microsoft 365 Copilot',
        text: 'Microsoft 365 Copilot does not use your company\'s data to train models. Customer data is processed within your Microsoft 365 tenant boundary and protected by existing compliance and security commitments.',
      },
    ],
  },
  {
    agent: 'Grok (xAI)',
    color: 'grok',
    source: 'xAI Privacy Policy & Consumer FAQs',
    url: 'https://x.ai/legal/privacy-policy',
    quotes: [
      {
        label: 'On training data (consumer)',
        text: "We may use your content and interactions with Grok (e.g., prompts, searches, and other materials you submit) along with Grok's responses to train our models. You control whether your data is used for training Grok.",
      },
      {
        label: 'On opting out',
        text: 'Once you select that your data should not be used for training, your new conversations will not be used to train our models. Alternatively, you can use Private Chat, and your content and interactions will not be included in model training.',
      },
      {
        label: 'On API / enterprise',
        text: 'xAI never trains on your API inputs or outputs without your explicit permission. By default, all API requests and responses are stored on our servers (encrypted at rest) for 30 days for auditing purposes... xAI does not train on this data, and it is automatically deleted after 30 days.',
      },
    ],
  },
  {
    agent: 'Meta AI',
    color: 'meta',
    source: 'Meta Privacy Policy',
    url: 'https://www.meta.com/legal/privacy-policy/',
    quotes: [
      {
        label: 'On training data',
        text: 'We may use the information you provide to improve our AI models, including conversations with Meta AI. This data may be used to train, fine-tune, and evaluate our generative AI models and features.',
      },
      {
        label: 'On opt-out limitations',
        text: 'Meta AI does not currently offer a global opt-out from model training for free-tier users. You may submit a data subject request, but Meta reserves the right to use data for model improvement as outlined in its privacy policy.',
      },
      {
        label: 'On data access',
        text: 'Meta AI has access to your public profile information and, depending on the platform, the context of your conversations. This data may be used to personalize responses and improve AI products.',
      },
    ],
  },
]

const colorMap = {
  claude: {
    header: 'bg-accent',
    border: 'border-accent/40',
    tag: 'bg-accent/15 text-accent border-accent/40',
    quote: 'border-accent/50',
  },
  chatgpt: {
    header: 'bg-emerald-700',
    border: 'border-emerald-700/40',
    tag: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
    quote: 'border-emerald-500/50',
  },
  gemini: {
    header: 'bg-amber-600',
    border: 'border-amber-600/40',
    tag: 'bg-amber-500/15 text-amber-200 border-amber-500/40',
    quote: 'border-amber-500/50',
  },
  copilot: {
    header: 'bg-sky-700',
    border: 'border-sky-700/40',
    tag: 'bg-sky-500/15 text-sky-300 border-sky-500/40',
    quote: 'border-sky-500/50',
  },
  meta: {
    header: 'bg-rose-700',
    border: 'border-rose-700/40',
    tag: 'bg-rose-500/15 text-rose-300 border-rose-500/40',
    quote: 'border-rose-500/50',
  },
  grok: {
    header: 'bg-zinc-600',
    border: 'border-zinc-500/40',
    tag: 'bg-zinc-400/15 text-zinc-300 border-zinc-400/40',
    quote: 'border-zinc-400/50',
  },
}

function getHighlight(val: string, invert = false): string {
  const v = val.trim()
  const favorable = 'bg-emerald-500/20 text-emerald-300'
  const unfavorable = 'bg-rose-500/15 text-rose-300'
  if (invert) {
    // Looser matching so qualified answers ("Yes (consumer plans)") pick up the color too.
    if (v.startsWith('Yes')) return unfavorable
    if (v.startsWith('No')) return favorable
  } else {
    if (v === 'Yes') return favorable
    if (v === 'No') return unfavorable
  }
  if (v === 'N/A' || v === 'Unclear') return 'bg-cream-faint text-cream-dim'
  if (v.startsWith('Limited')) return 'bg-amber-500/15 text-amber-200'
  return 'bg-bg-4 text-cream-dim'
}

export default function AIComparisonMatrix() {
  const [activeCategory, setActiveCategory] = useState<string | null>(null)
  const cats =
    activeCategory === null
      ? data.categories
      : data.categories.filter((c) => c.name === activeCategory)

  return (
    <>
      <PageMeta
        title={pageMeta.aiComparison.title}
        description={pageMeta.aiComparison.description}
        canonical={pageMeta.aiComparison.canonical}
      />
      <main className="min-h-screen pt-24 pb-16 px-6">
        <section className="max-w-4xl mx-auto text-center mb-12">
          <h1 className="font-display font-black text-4xl md:text-5xl uppercase text-white mb-6">
            AI Agent Comparison Matrix
          </h1>
          <p className="font-body text-cream-dim text-lg">
            Data verified as of September 2026. Plans and features change frequently — verify with each provider.
          </p>
        </section>

        <section className="max-w-6xl mx-auto mb-10">
          <div className="flex flex-wrap gap-2 justify-center">
            <button
              type="button"
              onClick={() => setActiveCategory(null)}
              className={`px-4 py-2 rounded text-sm font-body font-semibold uppercase tracking-wide border transition-colors ${
                activeCategory === null
                  ? 'bg-accent text-white border-accent'
                  : 'bg-bg-3 text-cream-dim border-accent-line hover:border-accent hover:text-white'
              }`}
            >
              All
            </button>
            {data.categories.map((c) => (
              <button
                key={c.name}
                type="button"
                onClick={() => setActiveCategory(c.name === activeCategory ? null : c.name)}
                className={`px-4 py-2 rounded text-sm font-body font-semibold uppercase tracking-wide border transition-colors ${
                  activeCategory === c.name
                    ? 'bg-accent text-white border-accent'
                    : 'bg-bg-3 text-cream-dim border-accent-line hover:border-accent hover:text-white'
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </section>

        <section className="max-w-6xl mx-auto">
          {cats.map((category) => (
            <div key={category.name} className="mb-10">
              <SectionLabel className="mb-3">{category.name}</SectionLabel>
              <div
                className="overflow-x-auto rounded-lg border"
                style={{ borderColor: 'var(--accent-line)' }}
              >
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-accent text-white">
                      <th className="text-left px-4 py-3 font-display font-bold uppercase tracking-wide w-52 sticky left-0 bg-accent z-10">
                        Feature
                      </th>
                      {data.agents.map((a) => (
                        <th
                          key={a}
                          className="text-center px-3 py-3 font-display font-bold uppercase tracking-wide min-w-32"
                        >
                          {a}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {category.rows.map((row, i) => {
                      const rowBg = i % 2 === 0 ? 'var(--bg-3)' : 'var(--bg-2)'
                      return (
                        <tr key={row.label} style={{ backgroundColor: rowBg }}>
                          <td
                            className="px-4 py-3 font-body font-medium text-cream sticky left-0 z-10"
                            style={{ backgroundColor: rowBg }}
                          >
                            {row.label}
                          </td>
                          {row.values.map((val, j) => (
                            <td key={j} className="px-3 py-3 text-center">
                              <span
                                className={`inline-block px-2 py-0.5 rounded text-xs font-body font-medium ${getHighlight(val, row.invertHighlight)}`}
                              >
                                {val}
                              </span>
                            </td>
                          ))}
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </section>

        <section className="max-w-6xl mx-auto mt-16 mb-16">
          <SectionLabel className="mb-3">Understanding Token Economics</SectionLabel>
          <div
            className="p-8 rounded-lg border-l-4"
            style={{ backgroundColor: 'var(--bg-3)', borderColor: 'var(--accent)' }}
          >
            <p className="font-body text-cream-dim mb-6">
              AI models bill API usage in tokens — chunks of text roughly three-quarters of a word each. Every
              request pays for input tokens (what you send, including documents and conversation history) and
              output tokens (what the model writes back). Output tokens cost 5–6x more than input tokens on most
              models, so long generated reports cost far more than long prompts.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="font-display font-black text-2xl mb-1" style={{ color: 'var(--accent)' }}>
                  ~750K words
                </div>
                <div className="font-body text-cream-dim text-sm">
                  What $10 buys in input on a flagship model (1M tokens)
                </div>
              </div>
              <div>
                <div className="font-display font-black text-2xl mb-1" style={{ color: 'var(--accent)' }}>
                  10–100x
                </div>
                <div className="font-body text-cream-dim text-sm">
                  Price spread between flagship and budget tiers for the same task
                </div>
              </div>
              <div>
                <div className="font-display font-black text-2xl mb-1" style={{ color: 'var(--accent)' }}>
                  Up to 90%
                </div>
                <div className="font-body text-cream-dim text-sm">
                  Input savings from prompt caching on repeated context
                </div>
              </div>
            </div>
            <p className="font-body text-cream-dim mb-0">
              The practical takeaway: model routing is where the money is. Sending routine work (classification,
              summaries, data extraction) to a budget model and reserving flagship models for complex reasoning
              typically cuts API spend 40–60% with no quality loss where it matters. Batch processing (50% off on
              most platforms) and prompt caching compound those savings for automated workflows.
            </p>
          </div>
        </section>

        <section className="max-w-6xl mx-auto mb-4">
          <SectionLabel className="mb-3">Privacy Policy — Supporting Quotes</SectionLabel>
          <p className="font-body text-cream-dim mb-8 max-w-3xl">
            Direct excerpts and paraphrased summaries from each provider&apos;s official privacy documentation
            regarding data training and security.{' '}
            <span className="text-cream font-medium">
              Always verify against current policy pages as these are updated frequently.
            </span>
          </p>

          <div className="grid grid-cols-1 gap-6">
            {privacyQuotes.map((provider) => {
              const c = colorMap[provider.color]
              return (
                <div
                  key={provider.agent}
                  className={`rounded-lg border ${c.border} overflow-hidden`}
                  style={{ backgroundColor: 'var(--bg-3)' }}
                >
                  <div className={`${c.header} px-5 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2`}>
                    <h3 className="font-display font-bold text-base uppercase text-white tracking-wide">
                      {provider.agent}
                    </h3>
                    <a
                      href={provider.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white/80 text-xs font-body underline hover:text-white"
                    >
                      {provider.source} ↗
                    </a>
                  </div>
                  <div className="divide-y divide-accent-line/40">
                    {provider.quotes.map((q) => (
                      <div key={q.label} className="px-5 py-4">
                        <span
                          className={`inline-block text-xs font-body font-semibold px-2 py-0.5 rounded border mb-2 ${c.tag}`}
                        >
                          {q.label}
                        </span>
                        <blockquote
                          className={`font-body text-sm text-cream-dim italic leading-relaxed border-l-4 pl-3 ml-1 ${c.quote}`}
                        >
                          &ldquo;{q.text}&rdquo;
                        </blockquote>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        </section>

        <p className="max-w-6xl mx-auto font-body text-xs text-cream-dim/60 mt-6 mb-16">
          * Quotes are sourced or closely paraphrased from each provider&apos;s official privacy policy or support
          documentation as of July 2026. Policies are subject to change — always review the current policy at each
          provider&apos;s website before making data privacy decisions.
        </p>

        <section className="max-w-3xl mx-auto text-center">
          <h2 className="font-display font-black text-3xl md:text-4xl uppercase text-white mb-4">
            Need help choosing the right AI tools for your business?
          </h2>
          <p className="font-body text-cream-dim mb-8">
            We configure AI with the security guardrails your team actually needs — scoped access, clear data
            boundaries, and workflows that save hours without putting client data at risk.
          </p>
          <Button to="/contact" variant="primary">
            Book a Free Automation Audit
          </Button>
        </section>
      </main>
    </>
  )
}

export { AIComparisonMatrix as Component }
